import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import SearchBar from "@/components/feature/SearchBar";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import FilterSidebar, { type FilterSection } from "@/components/feature/FilterSidebar";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { contentItems, videoCategories, videoDurations } from "@/mocks/content";
import { searchContent } from "@/utils/search";

const PAGE_SIZE = 6;
const DEMO_NOW = new Date("2026-10-14T00:00:00Z").getTime();

function dateOffset(days: number): string {
  const d = new Date(DEMO_NOW - days * 86400000);
  return d.toISOString().slice(0, 10);
}

const dateOptions = [
  { label: "全部时间", value: "全部" },
  { label: "近3天", value: "近3天" },
  { label: "近7天", value: "近7天" },
  { label: "更早", value: "更早" },
];

export default function Video() {
  const [params, setParams] = useSearchParams();
  const [showFilter, setShowFilter] = useState(false);

  const cat = params.get("cat") || "全部";
  const dur = params.get("dur") || "全部";
  const date = params.get("date") || "全部";
  const page = Number(params.get("page") || "1") || 1;

  let minDuration: number | undefined;
  let maxDuration: number | undefined;
  if (dur === "3分钟以内") maxDuration = 180;
  else if (dur === "3-10分钟") {
    minDuration = 181;
    maxDuration = 600;
  } else if (dur === "10分钟以上") minDuration = 601;

  let dateFrom: string | undefined;
  let dateTo: string | undefined;
  if (date === "近3天") dateFrom = dateOffset(3);
  else if (date === "近7天") dateFrom = dateOffset(7);
  else if (date === "更早") dateTo = dateOffset(8);

  const result = searchContent({
    type: "video",
    category: cat,
    sort: "time",
    page,
    pageSize: PAGE_SIZE,
    minDuration,
    maxDuration,
    dateFrom,
    dateTo,
  });

  useDocumentMeta({
    title: "视频分类 - 百度一下",
    description: "百度一下视频频道，提供科普、生活、教育、体育等多类别视频内容浏览，可按类别、时长与上传日期筛选。",
    keywords: "百度一下视频,视频分类,科普视频,视频推荐,短视频",
  });

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === "全部") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next);
  };

  const goPage = (p: number) => {
    const next = new URLSearchParams(params);
    if (p <= 1) next.delete("page");
    else next.set("page", String(p));
    setParams(next);
  };

  const countCat = (c: string) =>
    contentItems.filter((i) => i.type === "video" && (c === "全部" || i.category === c)).length;

  const sections: FilterSection[] = [
    {
      title: "视频类别",
      value: cat,
      options: videoCategories.map((c) => ({ label: c, value: c, count: countCat(c) })),
      onChange: (v) => updateParam("cat", v),
    },
    {
      title: "视频时长",
      value: dur,
      options: videoDurations.map((d) => ({ label: d, value: d })),
      onChange: (v) => updateParam("dur", v),
    },
    {
      title: "上传日期",
      value: date,
      options: dateOptions,
      onChange: (v) => updateParam("date", v),
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="w-full px-4 py-8 md:px-6 md:py-10">
            <div className="mx-auto max-w-6xl">
              <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                视频
              </h1>
              <p className="mt-2 text-sm text-foreground-600">
                精选科普、生活、教育等视频内容，支持按类别、时长与上传日期筛选。
              </p>
              <div className="mt-5">
                <SearchBar
                  placeholder="在视频频道内搜索关键词"
                  onSearch={(kw) => setParams({ q: kw, type: "video" })}
                />
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px_1fr]">
            <aside>
              <button
                type="button"
                onClick={() => setShowFilter((v) => !v)}
                className="mb-3 flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-700 lg:hidden"
              >
                <i className="ri-filter-3-line" />
                筛选视频
                <i className={`ri-arrow-down-s-line transition-transform ${showFilter ? "rotate-180" : ""}`} />
              </button>
              <div className="hidden lg:block">
                <FilterSidebar sections={sections} />
              </div>
              {showFilter && (
                <div className="lg:hidden">
                  <FilterSidebar sections={sections} />
                </div>
              )}
            </aside>

            <div className="min-w-0">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  {cat === "全部" ? "全部视频" : `${cat}视频`}
                </h2>
                <span className="text-sm text-foreground-500">共 {result.total} 个</span>
              </div>

              {result.items.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {result.items.map((item) => (
                      <VideoCard key={item.id} item={item} />
                    ))}
                  </div>
                  <Pagination current={result.page} totalPages={result.totalPages} onChange={goPage} />
                </>
              ) : (
                <EmptyState
                  icon="ri-film-line"
                  title="没有符合条件的视频"
                  description="试着放宽筛选条件，或看看下面的热门视频推荐。"
                  actions={
                    <>
                      <button
                        type="button"
                        onClick={() => setParams({})}
                        className="rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
                      >
                        清除筛选
                      </button>
                      <Link
                        to="/video?cat=科普"
                        className="rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 whitespace-nowrap transition-colors hover:border-primary-400 hover:text-primary-600"
                      >
                        查看热门科普视频
                      </Link>
                    </>
                  }
                />
              )}
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}